// @ts-ignore
import _ from 'lodash';
import { ajax } from 'utils';
import { PromiseAction } from '../types/promiseAction';
import * as api from 'services/javaApi';
import { PartnershipRequestInvoker } from '@purchase/supplier';
import { removeVendorStorage, setVendorStorage, getVendorStorage, isOuterIpService } from '@mpc/common/src/utils';

export const GET_USER_INFO_SUCCESS = 'mpc/account/get_user_info_success';
export const USER_LOGIN_OUT_SUCCESS = 'mpc/account/user_log_out_success';
export const GET_VENDOR_PROCESS_AUDIT_STATUS = 'mpc/account/get_vendor_process_audit_status';

export interface GetUserInfoSuccess {
  type: typeof GET_USER_INFO_SUCCESS;
  data?: object;
}
export interface UserLogoutSuccess {
  type: typeof USER_LOGIN_OUT_SUCCESS;
}

export type AccountActions = GetUserInfoSuccess | UserLogoutSuccess;

const setVendorRequestHeader = (result: any) => {
  const enterprises = result?.enterprises || [];
  // 设置默认供应商
  if (enterprises?.length) {
    let { vendorId: localVendorId, tenantCode: localTenantCode } = getVendorStorage();

    if (
      enterprises.some(
        (n: any) => n.vendorId === localVendorId && n.tenants?.some((t: any) => t.tenantCode === localTenantCode),
      )
    ) {
      return;
    }
    setVendorStorage(enterprises?.[0]?.vendorId || '', enterprises?.[0]?.tenants?.[0]?.tenantCode);
  }
};

/**
 * 版权方列表（{id,name,ro_code}）映射为供应商列表结构（vendorId/vendorName/tenants），
 * 复用 SupplierChoose 的扁平化与选择交互（IP 场景对齐外包切换供应商 UX）
 */
const mapCopyrightOwnersToSuppliers = (
  owners: Array<{ id?: number; name?: string; ro_code?: string }>,
): Array<Record<string, any>> => {
  return (owners || []).map((owner) => ({
    vendorId: String(owner.id),
    vendorName: owner.name,
    copyrightOwnerId: owner.id,
    roCode: owner.ro_code,
    // 不设置 tenants：空数组会让 SupplierChoose 的 flattenEnterprisesToList 走「有 tenants」分支，
    // 但空数组 forEach 不产出列表项 → 弹框列表为空。缺省 tenants 走 else 分支直接 push 自身
  }));
};

/**
 * 获取用户信息的公共逻辑
 * 合并 getUserData 和 outsourceAccountsUserInfo 的结果
 */
async function fetchUserInfoData() {
  try {
    if (isOuterIpService()) {
      // IP 服务不走中台，直接使用 IP 合作版权方用户信息接口（含 current_copyright_owner_id，FR-4）
      // 补齐 currentUser 结构（Main 依赖 currentUser 判断登录态；未登录时 data 为 null 触发跳转登录页）
      const userInfoResult = await api.ipCooperationAccountsUserInfo();
      const copyrightOwnersResult = await api.ipCooperationAccountsCopyrightOwners();
      return {
        ...userInfoResult,
        data: {
          ...(userInfoResult?.data || {}),
          currentUser: userInfoResult?.data || null,
          // 版权方列表映射为供应商列表结构，复用 SupplierChoose（IP 场景切换版权方，UX 对齐外包切换供应商）
          suppliersList: mapCopyrightOwnersToSuppliers(copyrightOwnersResult?.data || []),
        },
      };
    }
    const { data: userDataResult } =
      (await PartnershipRequestInvoker.getInstance().partnerShipOpenApiAccountCurrentUser()) as any;

    setVendorRequestHeader(userDataResult);

    let userInfoResult = await api.outsourceAccountsUserInfo();

    let vendorProcess = null;
    // 接口成功之后才进行路由hash的判断
    if(userDataResult?.enterprises){
      const hash = window.location.hash;
      let { vendorId: localVendorId, tenantCode: localTenantCode } = getVendorStorage();
      // 新供应商才能请求供应商详情接口 
      if (localVendorId?.indexOf('NEV') > -1) {
        try {
          vendorProcess = await PartnershipRequestInvoker.getInstance().partnerShipOpenApiVendorCheckStatusDetail();
          if (!vendorProcess?.data?.onboarded) {
            if (!hash.includes('#/outCompanyManagement/outCompanyInfo')) {
              window.location.hash = '#/outCompanyManagement/outCompanyInfo';
              window.location.reload();
              return;
            }
          }
        } catch (error) {}
      } else {
        if (hash.includes('#/outCompanyManagement/outCompanyInfo')) {
          window.location.hash = '#/outWorkbenchPortal/outNormalPurchase/pendingQuotePortal';
          window.location.reload();
          return;
        }
      }
    }

    return {
      ...userInfoResult,
      // 将 currentUser 和 vendorProcessAudit 合并到 data 中，确保 reducer 能获取到
      data: {
        ...(userInfoResult?.data || {}),
        currentUser: userDataResult, // 将 getUserData 的返回值添加到结果中
        suppliersList: userDataResult?.enterprises || [],
        vendorProcessAudit: vendorProcess?.data,
      },
    };
  } catch (error: any) {
    return {
      res_code: 0,
      res_msg: error.message || '获取用户信息失败',
      data: null,
    };
  }
}

export function getUserInfo(): PromiseAction {
  return {
    types: [null, GET_USER_INFO_SUCCESS, null],
    promise: fetchUserInfoData,
  };
}

export function userLogin(body: any): PromiseAction {
  return {
    types: [null, null, null],
    promise: () =>
      api.outsourceSessionsLogin(body).catch((error: any) => {
        return {
          res_code: 0,
          res_msg: error.message,
        };
      }),
  };
}

/**
 * 版权方（IP 合作）登录（FR-2）— 外包端 IP 登录页提交
 */
export function ipCooperationSessionsLogin(body: any): PromiseAction {
  return {
    types: [null, null, null],
    promise: () =>
      api.ipCooperationSessionsLogin(body).catch((error: any) => {
        return {
          res_code: 0,
          res_msg: error.message,
        };
      }),
  };
}

/**
 * 版权方（IP 合作）登出（FR-3）— 清除外包端登录态 cookie
 */
export function ipCooperationAccountsLogout(): PromiseAction {
  return {
    types: [null, USER_LOGIN_OUT_SUCCESS, null],
    promise: () =>
      api.ipCooperationSessionsLogout().then(() => {
        removeVendorStorage();
      }),
  };
}

/**
 * 版权方（IP 合作）关联版权方列表 — 复用 SupplierChoose（IP 场景对齐外包获取供应商列表）
 */
export function ipCooperationAccountsCopyrightOwners(): PromiseAction {
  return {
    types: [null, null, null],
    promise: () => api.ipCooperationAccountsCopyrightOwners(),
  };
}

/**
 * 版权方（IP 合作）切换版权方（重签 JWT，对标供应商切换）
 */
export function ipCooperationAccountsSwitch(body: any): PromiseAction {
  return {
    types: [null, null, null],
    promise: () => api.ipCooperationAccountsSwitch(body),
  };
}

export function userLogout(): PromiseAction {
  return {
    types: [null, USER_LOGIN_OUT_SUCCESS, null],
    promise: () =>
      isOuterIpService()
        ? api.ipCooperationSessionsLogout().then(() => {
            removeVendorStorage();
          })
        : PartnershipRequestInvoker.getInstance()
            .authLogout()
            .then(() => {
              removeVendorStorage();
            }),
  };
}

export function setLanguageTrunk(language: object) {
  return {
    types: [null, null, null],
    promise: () =>
      ajax({
        url: 'v1/accounts/set_language',
        method: 'POST',
        body: { language },
      }),
    language,
  };
}

export function setLanguage(language: object) {
  return (dispatch: any, getState: any) => {
    const id = _.get(getState(), `account.id`);
    return dispatch(setLanguageTrunk(language)).then((res: any) => {
      if (res.res_code === 1) {
        dispatch(getUserInfo(id));
      }
    });
  };
}

export function modifyPwd(body: any): PromiseAction {
  return {
    types: [null, null, null],
    promise: () => api.outsourceSessionsModifyPassword(body),
  };
}

export function forgetPwd(body: any): PromiseAction {
  return {
    types: [null, null, null],
    promise: () =>
      api.outsourceSessionsForgetPassword(body).catch((error: any) => {
        return {
          res_code: 0,
          res_msg: error.message,
        };
      }),
  };
}

export function resetPwd(body: any): PromiseAction {
  return {
    types: [null, null, null],
    promise: () =>
      api.outsourceSessionsResetPassword(body).catch((error: any) => {
        return {
          res_code: 0,
          res_msg: error.message,
        };
      }),
  };
}

export function checkLink(body: any): PromiseAction {
  return {
    types: [null, null, null],
    promise: () =>
      api.outsourceSessionsCheckResetLink(body).catch((error: any) => {
        return {
          res_code: -1,
          res_msg: error.message,
        };
      }),
  };
}

export function isOpenNotification(flag: boolean) {
  return (dispatch: any) => {
    return dispatch({
      type: 'flag',
      data: flag,
    });
  };
}
